import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { isSealedCategory } from "../../shared/market";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "../market/api";
import { Dialog, useArs } from "../market/components";
import { WantedPostCard } from "./components";

/** /se-busca/:slug — a card and each person looking for it (alias, never who), next to what's on sale. */
export function WantedCardPage() {
  const { t } = useI18n();
  const ars = useArs();
  const { slug = "" } = useParams();
  const { user } = useAuth();
  // Right after posting, skip the browser's cached copy so the new post shows.
  const location = useLocation();
  const fresh = Boolean((location.state as { fresh?: boolean } | null)?.fresh);
  // With the address it answered (`asked`): while the next card loads, the last one is still here.
  const data = useAsync(async (signal) => ({ asked: slug, ...(await marketApi.wantedCard(slug, signal, fresh)) }), [slug, fresh]);
  const [having, setHaving] = useState(false);
  const name = data.data?.product.name;
  // Only the answer to this address can say the card moved (not the last card, still loading the next).
  const current = data.data?.asked === slug ? data.data.product.slug : undefined;
  const navigate = useNavigate();

  // An old address (the card's slug changed): the URL becomes the card's own.
  useEffect(() => {
    if (current && current !== slug) navigate(`/se-busca/${encodeURIComponent(current)}${location.search}`, { replace: true });
  }, [current, slug, location.search, navigate]);

  useEffect(() => {
    if (name) document.title = `${t("wanted.cardTitle", { name })} | pokefans.fun`;
  }, [name, t]);

  if (data.error instanceof ApiError && data.error.status === 404) return <NotFoundPage />;
  if (data.error) return <ErrorMessage onRetry={data.reload} />;
  if (!data.data || data.data.asked !== slug) return <Loading />;
  const { product: p, posts, market } = data.data;
  const sealed = isSealedCategory(p.category);
  const meToo = user ? `/se-busca/publicar?carta=${encodeURIComponent(p.slug)}` : `/ingresar?next=${encodeURIComponent(`/se-busca/publicar?carta=${p.slug}`)}`;

  return (
    <div className="container section wanted-card-page">
      <p className="breadcrumbs">
        <Link to="/se-busca">{t("wanted.back")}</Link>
      </p>
      <div className="wanted-card-layout">
        <div className="wanted-card-main">
          <header className="wanted-card-head">
            <img src={imageUrl(p.imageKey)} alt={p.name} width={120} height={168} />
            <div>
              <h1>{t("wanted.cardTitle", { name: p.name })}</h1>
              <p className="muted">{[p.setName, p.cardNumber ? `#${p.cardNumber}` : null, p.rarity && p.rarity !== "None" ? p.rarity : null].filter(Boolean).join(" · ")}</p>
              {posts.length > 0 && <p className="wanted-count">{t("wanted.wanting", { count: posts.length })}</p>}
              <div className="btn-row">
                <button type="button" className="btn btn-primary" onClick={() => setHaving(true)}>
                  {t("wanted.iHaveIt")}
                </button>
                <Link to={meToo} className="btn btn-outline">
                  {t("wanted.meToo")}
                </Link>
              </div>
            </div>
          </header>
          {posts.length === 0 ? (
            <p className="muted">{t("wanted.noPosts")}</p>
          ) : (
            <div className="wanted-posts">
              {posts.map((w) => (
                <WantedPostCard key={w.id} post={w} sealed={sealed} />
              ))}
            </div>
          )}
        </div>
        <aside className="wanted-card-side">
          {market ? (
            <>
              <p>{t("wanted.marketHas", { count: market.count, price: ars(market.minPriceArs) })}</p>
              <Link to={`/producto/${p.slug}#ofertas`} className="btn btn-outline btn-block">
                {t("wanted.seeOffers")}
              </Link>
            </>
          ) : (
            <p className="muted">{t("wanted.marketNone")}</p>
          )}
          <Link to={`/producto/${encodeURIComponent(p.slug)}`} className="small">
            {p.name} →
          </Link>
        </aside>
      </div>
      {having && <HaveItDialog product={p} onClose={() => setHaving(false)} />}
    </div>
  );
}

/** "La tengo": no chat yet, so the way to the person is the market (they get the "Busco" alert). */
function HaveItDialog({ product, onClose }: { product: { id: number; slug: string; name: string; setName: string | null; cardNumber: string | null; rarity: string | null; imageKey: string; category: string }; onClose: () => void }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const shop = useAsync((signal) => (user ? marketApi.mySeller(signal) : Promise.resolve(null)), [user?.id]);
  const active = shop.data?.status === "active";
  return (
    <Dialog title={t(isSealedCategory(product.category) ? "wanted.haveTitleSealed" : "wanted.haveTitle")} onClose={onClose}>
      <p>{t("wanted.haveText")}</p>
      {!user ? (
        <Link to={`/ingresar?next=${encodeURIComponent(`/se-busca/${product.slug}`)}`} className="btn btn-primary">
          {t("wanted.signIn")}
        </Link>
      ) : shop.loading && !shop.data ? (
        <Loading />
      ) : active ? (
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              navigate("/mi-tienda/publicar", {
                state: { publish: { id: product.id, name: product.name, setName: product.setName, cardNumber: product.cardNumber, rarity: product.rarity, imageKey: product.imageKey, category: product.category } },
              })
            }
          >
            {t("wanted.haveSeller")}
          </button>
          <p className="muted small">{t("wanted.haveListed")}</p>
        </>
      ) : (
        <>
          <p className="muted small">{t("wanted.haveNoShop")}</p>
          <Link to="/vender" className="btn btn-primary">
            {t("wanted.openShop")}
          </Link>
        </>
      )}
      <p className="muted small">{t("wanted.tradeSoon")}</p>
      <button type="button" className="btn btn-link" onClick={onClose}>
        {t("nav.close")}
      </button>
    </Dialog>
  );
}
